import React, { useEffect, useMemo, useState } from 'react';
import {
    Checkbox,
    Label,
    Radio,
    RadioGroup,
    useId,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { app, pages } from '@microsoft/teams-js';
import { getBaseUrl } from '../../configVariables';
import { useConfigureTabPageStyles } from './ConfigureTabPage.styles';
import { loadConfig } from '../../store/features/config/configSlice';
import { useAppDispatch } from '../../store/hooks';
import { CustomerUpdatesViewType } from '../../store/features/customerUpdates/customerUpdatesSlice';
import { reportingTabsPageParamKeys } from '../ReportingPage/ReportingPage';
import { customerUpdatesPageParamKeys } from '../CustomerUpdatesPage/CustomerUpdatesPage';

interface TabParameterBase<T extends string = string> {
    name: string;
    label: string;
    defaultValue: T;
}

interface RadioTabParameter<
    T extends string = string,
> extends TabParameterBase<T> {
    type: 'radio';
    options: Array<{ value: T; labelKey: string }>;
}

interface CheckboxTabParameter extends TabParameterBase<'true' | 'false'> {
    type: 'checkbox';
}

type TabParameter = RadioTabParameter | CheckboxTabParameter;

interface TabConfig {
    entityId: string;
    suggestedDisplayName: string;
    path: string;
    parameters?: TabParameter[];
}

type AvailableTabs = { [key: string]: TabConfig };

const ConfigureTabPage = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const availableTabs = useMemo<AvailableTabs>(() => {
        const customerUpdatesViewTypeParam: RadioTabParameter<CustomerUpdatesViewType> =
            {
                name: customerUpdatesPageParamKeys.viewType,
                label: t('Configuration_CustomerUpdatesViewType_Label'),
                type: 'radio',
                defaultValue: 'All',
                options: [
                    {
                        value: 'All',
                        labelKey: 'Configuration_CustomerUpdates_All',
                    },
                    {
                        value: 'MishandledBaggage',
                        labelKey:
                            'Configuration_CustomerUpdates_MishandledBaggage',
                    },
                    {
                        value: 'MisconnectedCustomers',
                        labelKey:
                            'Configuration_CustomerUpdates_MisconnectedCustomers',
                    },
                ],
            };

        const tabs: AvailableTabs = {
            BA_Derarture_Board: {
                entityId: 'BA_Derarture_Board',
                suggestedDisplayName: t(
                    'Configuration_Tab_BA_Departure_Board_DisplayName'
                ),
                path: 'departureBoard',
            },
            BA_App_Configuration_Board: {
                entityId: 'BA_App_Configuration_Board',
                suggestedDisplayName: t(
                    'Configuration_Tab_BA_App_Configuration_Board_DisplayName'
                ),
                path: 'appConfiguration',
            },
            BA_Reporting: {
                entityId: 'BA_Reporting',
                suggestedDisplayName: t(
                    'Configuration_Tab_BA_Reporting_DisplayName'
                ),
                path: 'reporting',
                parameters: [
                    {
                        name: reportingTabsPageParamKeys.allTeamsInDashboard,
                        label: t(
                            'Configuration_Tab_BA_Reporting_Dashboard_AllTeamsParamDisplayName'
                        ),
                        type: 'checkbox',
                        defaultValue: 'false',
                    },
                ],
            },
            BA_Active_Conversations: {
                entityId: 'BA_Active_Conversations',
                suggestedDisplayName: t(
                    'Configuration_Tab_BA_Active_Conversations_DisplayName'
                ),
                path: 'activeConversations',
            },
            BA_Health_Dashboard: {
                entityId: 'BA_Health_Dashboard',
                suggestedDisplayName: t(
                    'Configuration_Tab_BA_Health_Dashboard_DisplayName'
                ),
                path: 'healthDashboard',
            },
            BA_Conversation_Ratings: {
                entityId: 'BA_Conversation_Ratings',
                suggestedDisplayName: t(
                    'Configuration_Tab_BA_Conversation_Ratings_DisplayName'
                ),
                path: 'conversationRatings',
            },
            BA_Flight_Baggage_Status: {
                entityId: 'BA_Flight_Baggage_Status',
                suggestedDisplayName: t(
                    'Configuration_BA_Flight_Baggage_Status_DisplayName'
                ),
                path: 'flightBaggageStatus',
                parameters: [customerUpdatesViewTypeParam],
            },
        };

        return tabs;
    }, [t]);

    const [tabId, setTabId] = React.useState('');

    const [parameterValues, setParameterValues] = useState<
        Record<string, string>
    >({});

    const mainLabelId = useId('label');
    const styles = useConfigureTabPageStyles();

    // Initialize parameter values when tab changes
    useEffect(() => {
        const tab = availableTabs[tabId];
        if (tab?.parameters) {
            const defaults: Record<string, string> = {};
            tab.parameters.forEach((param) => {
                defaults[param.name] = param.defaultValue;
            });
            setParameterValues(defaults);
        } else {
            setParameterValues({});
        }
    }, [tabId, availableTabs]);

    useEffect(() => {
        dispatch(loadConfig());
        const init = async () => {
            const config = await pages.getConfig();
            if (config && config.entityId) {
                setTabId(config.entityId);
            }
        };

        init().then(() => {
            app.notifySuccess();
        });
    }, [dispatch]);

    useEffect(() => {
        if (!tabId) {
            pages.config.setValidityState(false);
            return;
        }

        const tab = availableTabs[tabId];
        if (!tab) {
            return;
        }

        // Build contentUrl with parameters dynamically
        let contentUrl = `${getBaseUrl()}/${tab.path}?locale={locale}`;

        if (tab.parameters && Object.keys(parameterValues).length > 0) {
            const paramString = Object.entries(parameterValues)
                .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
                .join('&');
            contentUrl += `&${paramString}`;
        }

        pages.config.registerOnSaveHandler((saveEvent) => {
            pages.config.setConfig({
                entityId: tab.entityId,
                suggestedDisplayName: tab.suggestedDisplayName,
                contentUrl,
            });
            saveEvent.notifySuccess();
        });

        pages.config.setValidityState(true);
    }, [tabId, availableTabs, parameterValues]);

    return (
        <div className={styles.root}>
            <div className={styles.field}>
                <Label id={mainLabelId}>{t('Configuration_Tabs_Label')}</Label>
                <RadioGroup
                    value={tabId}
                    onChange={(_, data) => setTabId(data.value)}
                    aria-labelledby={mainLabelId}
                >
                    {Object.keys(availableTabs).map((k) => (
                        <Radio
                            key={k}
                            value={k}
                            label={availableTabs[k].suggestedDisplayName}
                        />
                    ))}
                </RadioGroup>
            </div>
            {/* Dynamically render parameter forms based on tab configuration */}
            {availableTabs[tabId]?.parameters?.map((param) => {
                if (param.type === 'radio') {
                    return (
                        <div key={param.name} className={styles.field}>
                            <Label>{param.label}</Label>
                            <RadioGroup
                                value={
                                    parameterValues[param.name] ||
                                    param.defaultValue
                                }
                                onChange={(_, data) =>
                                    setParameterValues((prev) => ({
                                        ...prev,
                                        [param.name]: data.value,
                                    }))
                                }
                            >
                                {param.options.map((option) => (
                                    <Radio
                                        key={option.value}
                                        value={option.value}
                                        label={t(option.labelKey)}
                                    />
                                ))}
                            </RadioGroup>
                        </div>
                    );
                }

                if (param.type === 'checkbox') {
                    return (
                        <div key={param.name} className={styles.field}>
                            <Checkbox
                                label={param.label}
                                checked={
                                    (parameterValues[param.name] ||
                                        param.defaultValue) === 'true'
                                }
                                onChange={(_ev, data) =>
                                    setParameterValues((prev) => ({
                                        ...prev,
                                        [param.name]: data.checked
                                            ? 'true'
                                            : 'false',
                                    }))
                                }
                            />
                        </div>
                    );
                }
                return null;
            })}
        </div>
    );
};

export default ConfigureTabPage;
